import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  BarChart3,
  PartyPopper,
  CheckCircle2,
  Clock3,
  Download,
  Mail,
  XCircle,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  API_ORIGIN,
  apiDeleteEvent,
  apiExportGuestsCsv,
  apiListEventGuests,
  apiListMyEvents,
  apiPublishEvent,
  apiSendReminders,
  apiUpdateEvent,
} from '../../services/api';

const SOCKET_URL = API_ORIGIN;

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function buildPieBackground(summary) {
  const total = summary.confirmed + summary.pending + summary.declined;
  if (!total) {
    return 'conic-gradient(#e8ecef 0% 100%)';
  }

  const confirmedPct = (summary.confirmed / total) * 100;
  const pendingPct = (summary.pending / total) * 100;
  const declinedPct = 100 - confirmedPct - pendingPct;

  return `conic-gradient(#4ecdc4 0 ${confirmedPct}%, #ffd700 ${confirmedPct}% ${confirmedPct + pendingPct}%, #ff6b6b ${confirmedPct + pendingPct}% ${confirmedPct + pendingPct + declinedPct}%)`;
}

export default function Dashboard() {
  const { tab } = useParams();
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [guests, setGuests] = useState([]);
  const [summary, setSummary] = useState({ confirmed: 0, pending: 0, declined: 0, attending_total: 0 });
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingEventId, setEditingEventId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    eventDate: '',
    location: '',
    theme: '',
    description: '',
    maxGuests: 50,
    isPublished: true,
  });

  useEffect(() => {
    async function loadEvents() {
      setLoading(true);
      const data = await apiListMyEvents(token);
      if (data.events) {
        setEvents(data.events);
        if (data.events[0]) {
          setSelectedEventId(data.events[0].id);
        }
      }
      setLoading(false);
    }

    loadEvents();
  }, [token]);

  useEffect(() => {
    if (!selectedEventId) {
      setGuests([]);
      setSummary({ confirmed: 0, pending: 0, declined: 0, attending_total: 0 });
      return;
    }

    async function loadGuests() {
      const data = await apiListEventGuests(selectedEventId, token);
      if (data.guests) {
        setGuests(data.guests);
      }
      if (data.summary) {
        setSummary({
          confirmed: Number(data.summary.confirmed || 0),
          pending: Number(data.summary.pending || 0),
          declined: Number(data.summary.declined || 0),
          attending_total: Number(data.summary.attending_total || 0),
        });
      }
    }

    loadGuests();
  }, [selectedEventId, token]);

  useEffect(() => {
    if (!selectedEventId) return undefined;

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socket.emit('join_event', selectedEventId);

    socket.on('rsvp:updated', (payload) => {
      if (!payload || payload.eventId !== selectedEventId) return;

      if (payload.summary) {
        setSummary({
          confirmed: Number(payload.summary.confirmed || 0),
          pending: Number(payload.summary.pending || 0),
          declined: Number(payload.summary.declined || 0),
          attending_total: Number(payload.summary.attending_total || 0),
        });
      }

      if (payload.rsvp) {
        setGuests((prev) => {
          const index = prev.findIndex((item) => item.id === payload.rsvp.id);
          if (index === -1) return [payload.rsvp, ...prev];

          const next = [...prev];
          next[index] = payload.rsvp;
          return next;
        });
      }
    });

    return () => {
      socket.emit('leave_event', selectedEventId);
      socket.disconnect();
    };
  }, [selectedEventId]);

  const selectedEvent = useMemo(
    () => events.find((event) => event.id === selectedEventId),
    [events, selectedEventId]
  );

  const selectedInviteLink = useMemo(() => {
    if (!selectedEvent?.slug) return '';
    const base = window.location.origin.replace(/\/$/, '');
    return `${base}/convite/${selectedEvent.slug}`;
  }, [selectedEvent]);

  const selectedQrCodeUrl = useMemo(() => {
    if (!selectedInviteLink) return '';
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(selectedInviteLink)}`;
  }, [selectedInviteLink]);

  const totalGuests = guests.length;
  const pieBackground = buildPieBackground(summary);
  const activeTab = ['meus-eventos', 'convites', 'relatorios', 'configuracoes'].includes(tab)
    ? tab
    : 'meus-eventos';

  const showToast = (value) => {
    setToast(value);
    setTimeout(() => setToast(''), 2600);
  };

  const handleExportCsv = async () => {
    if (!selectedEventId) return;
    const data = await apiExportGuestsCsv(selectedEventId, token);
    if (!data.ok) {
      showToast('Não foi possível exportar agora.');
      return;
    }

    downloadBlob(data.blob, 'convidados.csv');
    showToast('CSV exportado com sucesso.');
  };

  const handlePublishToggle = async () => {
    if (!selectedEvent) return;

    const response = await apiPublishEvent(selectedEvent.id, !selectedEvent.is_published, token);
    if (response.error) {
      showToast(response.error);
      return;
    }

    setEvents((prev) => prev.map((event) => (
      event.id === selectedEvent.id
        ? { ...event, is_published: response.event.is_published }
        : event
    )));

    showToast(response.message || 'Visibilidade atualizada.');
  };

  const handleSendReminders = async () => {
    if (!selectedEventId) return;
    const response = await apiSendReminders(selectedEventId, token);

    if (response.error) {
      showToast(response.error);
      return;
    }

    showToast(`Lembretes enviados: ${response.deliveredCount}/${response.totalGuests}`);
  };

  const openEditForm = (event) => {
    setSelectedEventId(event.id);
    setEditingEventId(event.id);
    setDeleteConfirmId(null);
    navigate('/dashboard/configuracoes');
    setEditForm({
      title: event.title || '',
      eventDate: event.event_date ? new Date(event.event_date).toISOString().slice(0, 16) : '',
      location: event.location || '',
      theme: event.theme || '',
      description: event.description || '',
      maxGuests: event.max_guests || 50,
      isPublished: Boolean(event.is_published),
    });
  };

  const handleEditInputChange = (field) => (event) => {
    const value = field === 'isPublished' ? event.target.checked : event.target.value;
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateEvent = async (event) => {
    event.preventDefault();
    if (!editingEventId) return;

    const response = await apiUpdateEvent(editingEventId, editForm, token);
    if (response.error) {
      showToast(response.error);
      return;
    }

    setEvents((prev) => prev.map((item) => (item.id === editingEventId ? { ...item, ...response.event } : item)));
    setSelectedEventId(editingEventId);
    setEditingEventId(null);
    showToast(response.message || 'Evento atualizado.');
  };

  const handleDeleteEvent = async (eventId) => {
    const eventToDelete = events.find((item) => item.id === eventId);
    if (!eventToDelete) return;

    if (deleteConfirmId !== eventId) {
      setDeleteConfirmId(eventId);
      return;
    }

    const response = await apiDeleteEvent(eventId, token);
    if (response.error) {
      showToast(response.error);
      return;
    }

    setEvents((prev) => prev.filter((item) => item.id !== eventId));
    if (selectedEventId === eventId) {
      setSelectedEventId(null);
    }
    setDeleteConfirmId(null);
    showToast(response.message || 'Evento excluído.');
  };

  return (
    <div className="dashboard-layout">
      <header className="dashboard-topbar">
        <Link to="/" className="brand-mark">
          <span className="brand-icon" aria-hidden="true">
            <svg viewBox="0 0 48 48">
              <circle cx="24" cy="24" r="20" fill="url(#brandGradient)" />
              <path d="M16 12 H28 V16 H20 V22 H27 V26 H20 V36 H16 Z" fill="#0f243e" />
              <circle cx="31" cy="14" r="4.4" fill="#d9eeff" />
              <path d="M31 19 L29.4 21.2 L32.6 21.2 Z" fill="#bfe2ff" />
              <path d="M31 21.2 L31 30" stroke="#0f243e" strokeWidth="1.7" strokeLinecap="round" />
              <path d="M31 25.5 L27.2 21.2" stroke="#0f243e" strokeWidth="1.7" strokeLinecap="round" />
              <path d="M31 25.5 L34.8 21.2" stroke="#0f243e" strokeWidth="1.7" strokeLinecap="round" />
              <defs>
                <linearGradient id="brandGradient" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#7ec3ff" />
                  <stop offset="100%" stopColor="#2c8fff" />
                </linearGradient>
              </defs>
            </svg>
          </span>
          <span className="brand-name">Festify</span>
        </Link>

        <nav className="dashboard-menu">
          <NavLink to="/dashboard/meus-eventos"><PartyPopper size={18} /> Meus Eventos</NavLink>
          <NavLink to="/dashboard/convites"><CheckCircle2 size={18} /> Convites</NavLink>
          <NavLink to="/dashboard/relatorios"><BarChart3 size={18} /> Relatórios</NavLink>
          <NavLink to="/dashboard/configuracoes"><Clock3 size={18} /> Configurações</NavLink>
        </nav>

        <div className="topbar-right">
          <p>{user?.email}</p>
          <button type="button" className="ghost-btn" onClick={logout}>Sair</button>
        </div>
      </header>

      <main className="dashboard-main">
        <header className="festify-page-header">
          <div>
            <p className="festify-eyebrow">Painel do organizador</p>
            <h1>Seus convites em tempo real</h1>
          </div>
          <div className="festify-header-actions">
            <Link className="submit-btn compact" to="/evento/novo">Novo evento</Link>
          </div>
        </header>

        {loading && <p className="loading-text">Carregando eventos...</p>}

        {!loading && activeTab === 'meus-eventos' && (
          <section className="dashboard-grid">
            <article className="festify-form-card">
              <h2>Meus eventos</h2>
              <div className="event-list">
                {events.length === 0 && <p>Nenhum evento cadastrado ainda.</p>}
                {events.map((event) => (
                  <div key={event.id} className={event.id === selectedEventId ? 'event-item active' : 'event-item'}>
                    <button type="button" className="event-select-btn" onClick={() => setSelectedEventId(event.id)}>
                      <strong>{event.title}</strong>
                      <span>{new Date(event.event_date).toLocaleDateString('pt-BR')}</span>
                      <small>{event.location}</small>
                    </button>
                    <div className="event-actions">
                      <button type="button" className="ghost-btn compact" onClick={() => openEditForm(event)}>Editar</button>
                      <button type="button" className="ghost-btn compact danger" onClick={() => handleDeleteEvent(event.id)}>
                        {deleteConfirmId === event.id ? 'Confirmar exclusão' : 'Excluir'}
                      </button>
                    </div>
                    {deleteConfirmId === event.id && (
                      <p className="delete-confirmation">Confirme para excluir este evento.</p>
                    )}
                  </div>
                ))}
              </div>
            </article>

            <article className="festify-form-card invite-result">
              <h2>Convite do evento selecionado</h2>
              {!selectedEvent && <p>Selecione um evento para ver o link e QR Code.</p>}
              {selectedEvent && (
                <>
                  <a href={selectedInviteLink} target="_blank" rel="noreferrer" className="invite-link">
                    {selectedInviteLink}
                  </a>
                  <div className="qr-wrapper">
                    {selectedQrCodeUrl && <img src={selectedQrCodeUrl} alt="QR Code do convite selecionado" />}
                  </div>
                </>
              )}
            </article>
          </section>
        )}

        {!loading && activeTab === 'convites' && (
          <section className="festify-form-card table-wrap">
            <h2>Convidados do evento</h2>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>WhatsApp</th>
                    <th>Acompanhantes</th>
                    <th>Status</th>
                    <th>Restrições</th>
                  </tr>
                </thead>
                <tbody>
                  {guests.length === 0 && (
                    <tr>
                      <td colSpan="6">Nenhum RSVP registrado.</td>
                    </tr>
                  )}
                  {guests.map((guest) => (
                    <tr key={guest.id}>
                      <td>{guest.guest_name}</td>
                      <td>{guest.guest_email || '-'}</td>
                      <td>{guest.guest_whatsapp || '-'}</td>
                      <td>{guest.companions}</td>
                      <td>{guest.status}</td>
                      <td>{guest.dietary_restrictions || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {!loading && activeTab === 'relatorios' && (
          <section className="festify-form-card">
            <h2>Indicadores</h2>
            <div className="stats-row">
              <div className="stat-card"><p>Total</p><strong>{totalGuests}</strong></div>
              <div className="stat-card"><p>✅ Confirmados</p><strong>{summary.confirmed}</strong></div>
              <div className="stat-card"><p>👥 Pessoas confirmadas</p><strong>{summary.attending_total}</strong></div>
              <div className="stat-card"><p>⏳ Pendentes</p><strong>{summary.pending}</strong></div>
              <div className="stat-card"><p>❌ Recusados</p><strong>{summary.declined}</strong></div>
            </div>

            <div className="chart-wrap">
              <div className="pie-chart" style={{ background: pieBackground }} />
              <p>Distribuição de confirmações</p>
            </div>
          </section>
        )}

        {!loading && activeTab === 'configuracoes' && (
          <section className="festify-form-card">
            <h2>Configurações do evento selecionado</h2>
            {editingEventId && selectedEvent && (
              <form className="edit-event-form" onSubmit={handleUpdateEvent}>
                <div className="grid-two">
                  <label>
                    Título
                    <input value={editForm.title} onChange={handleEditInputChange('title')} required />
                  </label>
                  <label>
                    Data e hora
                    <input type="datetime-local" value={editForm.eventDate} onChange={handleEditInputChange('eventDate')} required />
                  </label>
                </div>
                <div className="grid-two">
                  <label>
                    Local
                    <input value={editForm.location} onChange={handleEditInputChange('location')} required />
                  </label>
                  <label>
                    Tema
                    <input value={editForm.theme} onChange={handleEditInputChange('theme')} />
                  </label>
                </div>
                <div className="grid-two">
                  <label>
                    Máximo de convidados
                    <input type="number" min="1" value={editForm.maxGuests} onChange={handleEditInputChange('maxGuests')} />
                  </label>
                  <label className="checkbox-row">
                    <input type="checkbox" checked={editForm.isPublished} onChange={handleEditInputChange('isPublished')} />
                    Publicar imediatamente
                  </label>
                </div>
                <label>
                  Descrição
                  <textarea rows="3" value={editForm.description} onChange={handleEditInputChange('description')} />
                </label>
                <div className="action-row">
                  <button type="submit" className="submit-btn compact">Salvar alterações</button>
                  <button type="button" className="ghost-btn compact" onClick={() => setEditingEventId(null)}>Cancelar</button>
                </div>
              </form>
            )}
            <div className="action-row">
              <button type="button" className="ghost-btn" onClick={handleExportCsv}>
                <Download size={16} /> Exportar CSV
              </button>
              <button type="button" className="ghost-btn" onClick={handleSendReminders}>
                <Mail size={16} /> Enviar lembrete
              </button>
              <button type="button" className="ghost-btn" onClick={handlePublishToggle}>
                {selectedEvent?.is_published ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
                {selectedEvent?.is_published ? 'Despublicar' : 'Publicar alterações'}
              </button>
            </div>
          </section>
        )}

        {toast && <div className="toast">{toast}</div>}
      </main>
    </div>
  );
}
